import { useEffect, useState } from "react";
import type { BrandingTemplate, LeaderboardEntry, RaceStanding } from "@vms/types";

/**
 * Public (unauthenticated) fetch hooks for the per-event leaderboard + driver
 * results pages, gated by the event's `public_key`. Like
 * `public-leaderboard.ts` these deliberately bypass `useApi()`/JWT and the
 * cloud UI WebSocket — the visitor has no session — so they poll instead.
 */

export interface PublicEventMeta {
  id: string;
  name: string;
  mode: "hotlap" | "race";
  track: string | null;
  trackName: string | null;
  game: string | null;
  /** The event's configured cars — drives whether the leaderboard shows the model. */
  carsAvailable?: string[];
}

export interface PublicEventConfig {
  event: PublicEventMeta;
  branding: BrandingTemplate | null;
}

export interface PublicEventEntries {
  mode: "hotlap" | "race";
  entries: LeaderboardEntry[] | RaceStanding[];
}

export interface FastestSectors {
  sector1Ms: number | null;
  sector2Ms: number | null;
  sector3Ms: number | null;
}

async function fetchPublic<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const json = (await res.json()) as { success?: boolean; data?: T };
    if (!json?.success) return null;
    return (json.data ?? null) as T | null;
  } catch {
    return null;
  }
}

/** Fetch the event meta + branding once. `notFound` distinguishes a bad/expired
 *  key (404) from a still-loading state so the page can show a clear message. */
export function usePublicEventConfig(
  key: string | undefined,
): { data: PublicEventConfig | null; notFound: boolean } {
  const [data, setData] = useState<PublicEventConfig | null>(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    setNotFound(false);
    void (async () => {
      const c = await fetchPublic<PublicEventConfig>(
        `/api/public/event/${encodeURIComponent(key)}`,
      );
      if (cancelled) return;
      if (c) setData(c);
      else setNotFound(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);
  return { data, notFound };
}

/** Fetch (and optionally poll) the mode-aware standings. `pollMs <= 0` fetches
 *  once with no interval — used as the fallback when the live WS is connected. */
export function usePublicEventEntries(
  key: string | undefined,
  pollMs = 8000,
): { data: PublicEventEntries | null } {
  const [data, setData] = useState<PublicEventEntries | null>(null);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const tick = async () => {
      const d = await fetchPublic<PublicEventEntries>(
        `/api/public/event/${encodeURIComponent(key)}/entries`,
      );
      if (!cancelled && d) setData(d);
    };
    void tick();
    const handle = pollMs > 0 ? setInterval(() => void tick(), pollMs) : null;
    return () => {
      cancelled = true;
      if (handle) clearInterval(handle);
    };
  }, [key, pollMs]);
  return { data };
}

/**
 * Live event leaderboard over the public WebSocket (`/public-ws`). Subscribes by
 * the event key; the cloud pushes the recomputed board on change (coalesced),
 * so no polling while connected. Auto-reconnects with backoff. `connected`
 * lets the page fall back to polling when the socket is down.
 */
export function usePublicLeaderboardLive(key: string | undefined): {
  data: PublicEventEntries | null;
  sectors: FastestSectors | null;
  connected: boolean;
} {
  const [data, setData] = useState<PublicEventEntries | null>(null);
  const [sectors, setSectors] = useState<FastestSectors | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!key) return;
    let closedByUs = false;
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = 2000;

    const connect = () => {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${protocol}//${window.location.host}/public-ws`);
      ws.onopen = () => {
        backoff = 2000;
        setConnected(true);
        try {
          ws?.send(JSON.stringify({ type: "subscribe", key }));
        } catch {
          /* ignore */
        }
      };
      ws.onmessage = (ev) => {
        let msg: {
          type?: string;
          mode?: "hotlap" | "race";
          entries?: LeaderboardEntry[] | RaceStanding[];
          sectors?: FastestSectors;
        };
        try {
          msg = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        if (msg?.type === "leaderboard") {
          setData({ mode: msg.mode ?? "hotlap", entries: msg.entries ?? [] });
          if (msg.sectors) setSectors(msg.sectors);
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedByUs) return;
        reconnectTimer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 30000);
      };
      ws.onerror = () => {
        /* onclose follows and handles reconnect */
      };
    };
    connect();

    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
    };
  }, [key]);

  return { data, sectors, connected };
}

/** Fetch (and optionally poll) fastest sectors. `pollMs <= 0` fetches once. */
export function usePublicEventSectors(
  key: string | undefined,
  pollMs = 8000,
): { data: FastestSectors | null } {
  const [data, setData] = useState<FastestSectors | null>(null);
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const tick = async () => {
      const d = await fetchPublic<FastestSectors>(
        `/api/public/event/${encodeURIComponent(key)}/sectors`,
      );
      if (!cancelled && d) setData(d);
    };
    void tick();
    const handle = pollMs > 0 ? setInterval(() => void tick(), pollMs) : null;
    return () => {
      cancelled = true;
      if (handle) clearInterval(handle);
    };
  }, [key, pollMs]);
  return { data };
}
